import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import dayjs from '~/timeInit';

import { Box, Button, Stack } from '@mui/material';

import { downloadFlightRefunds } from '~/services/ReportingService';

import Spinner from '../Spinner';

const today = dayjs().format('YYYY-MM-DD');

function validate(formData, errors) {
  const todayString = today;

  let startDateValid = true;
  let endDateValid = true;

  if (formData.startAt) {
    if (formData.startAt > todayString) {
      errors.startAt.addError('Start date cannot be in the future');
      startDateValid = false;
    }
  }

  if (formData.endAt) {
    if (formData.endAt > todayString) {
      errors.endAt.addError('End date cannot be in the future');
      endDateValid = false;
    }
  }

  if (formData.startAt && formData.endAt && startDateValid && endDateValid) {
    if (formData.startAt > formData.endAt) {
      errors.startAt.addError('Start date must be before or equal to end date');
    }
  }

  return errors;
}

const formSchema: RJSFSchema = {
  description: 'Please enter your dates to export the Flight Refunds report',
  type: 'object',
  required: ['startAt', 'endAt'],
  properties: {
    startAt: {
      type: 'string',
      format: 'date',
      title: 'Start Date (Inclusive)',
    },
    endAt: {
      type: 'string',
      format: 'date',
      title: 'End Date (Inclusive)',
    },
    flightType: {
      type: 'array',
      title: 'Flight Type',
      items: {
        type: 'string',
        enum: ['All Flights', 'Online Flights', 'Offline Flights', 'Custom Offer Flights'],
      },
      uniqueItems: true,
    },
    fileFormat: {
      type: 'string',
      title: 'File Format',
      enum: ['csv', 'email'],
    },
  },
};

const initialFormData = {
  startAt: '',
  endAt: '',
  flightType: ['All Flights'],
  fileFormat: 'csv',
};

const getUiSchema = (isAllFlightsSelected: boolean) => ({
  startAt: {
    'ui:widget': 'date',
    'ui:options': {
      inputProps: {
        max: today,
      },
    },
  },
  endAt: {
    'ui:widget': 'date',
    'ui:options': {
      inputProps: {
        max: today,
      },
    },
  },
  flightType: {
    'ui:widget': 'checkboxes',
    'ui:options': {
      enumDisabled: isAllFlightsSelected ? ['Online Flights', 'Offline Flights', 'Custom Offer Flights'] : [],
    },
  },
  fileFormat: {
    'ui:widget': 'radio',
  },
});

const flightTypeMapping: Record<string, string> = {
  'All Flights': 'all_flights',
  'Online Flights': 'online_flights',
  'Offline Flights': 'offline_flights',
  'Custom Offer Flights': 'custom_offer_flights',
};

function ExportFlightRefundsForm() {
  const { enqueueSnackbar } = useSnackbar();
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [hasEditedDates, setHasEditedDates] = useState(false);

  const handleChange = (event: IChangeEvent) => {
    const updatedFormData = { ...event.formData };

    if (updatedFormData.flightType?.includes('All Flights')) {
      updatedFormData.flightType = ['All Flights'];
    }

    setFormData(updatedFormData);

    if (
      (updatedFormData.startAt && updatedFormData.startAt !== initialFormData.startAt) ||
      (updatedFormData.endAt && updatedFormData.endAt !== initialFormData.endAt)
    ) {
      setHasEditedDates(true);
    }
  };

  const handleSubmit = (event: IChangeEvent) => {
    const { startAt, endAt, flightType, fileFormat } = event.formData;

    const mappedFlightTypes: Array<string> = (flightType || []).map((type: string) => flightTypeMapping[type] || type);

    setIsGenerating(true);

    const data = downloadFlightRefunds({
      startAt,
      endAt,
      flightTypes: mappedFlightTypes,
      fileFormat,
    });

    data
      .then((blob) => {
        if (fileFormat.toLowerCase() !== 'email') {
          const flightTypesStr = mappedFlightTypes.join('_');
          const fileName = `flight_refunds_report_${startAt}_${endAt}_${flightTypesStr}.${fileFormat}`;
          fileDownload(blob, fileName);
          enqueueSnackbar(`Your report has been downloaded`, { variant: 'success' });
        } else {
          enqueueSnackbar(`Your report is queued for delivery and will be emailed shortly.`, { variant: 'success' });
        }
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const isAllFlightsSelected = formData.flightType?.includes('All Flights');

  return (
    <Box>
      <Form
        schema={formSchema}
        formData={formData}
        uiSchema={getUiSchema(isAllFlightsSelected)}
        validator={validator}
        customValidate={validate}
        onSubmit={handleSubmit}
        onChange={handleChange}
        className="package-form"
        disabled={isGenerating}
        showErrorList={false}
        liveValidate={hasEditedDates}
      >
        <Button type="submit" variant="contained" disabled={isGenerating}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>Download/Email Flight Refunds Report</Box>
            {isGenerating && <Spinner color="white" size={20} inline />}
          </Stack>
        </Button>
      </Form>
    </Box>
  );
}

export default ExportFlightRefundsForm;
